import sum from 'lodash/sum';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { useOrderingV2Ui } from 'src/contexts/ordering-v2-ui-context';
import { useCheckout } from 'src/sections/product/hooks';
import { paths } from 'src/routes/paths';
import { buildOrderingCheckoutQuery } from 'src/utils/order-checkout-params';
import { fCurrency } from 'src/utils/format-number';
import CheckoutCartProductList from 'src/sections/product/checkout/checkout-cart-product-list';
import OrderV2ProductCard from './order-v2-product-card';

// ----------------------------------------------------------------------

export default function OrderV2CartDrawer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartDrawerOpen, closeCart, openProductDetail, crossSellProducts } = useOrderingV2Ui();
  const {
    checkout,
    onDeleteCart,
    onIncreaseQuantity,
    onDecreaseQuantity,
  } = useCheckout();

  const { cart, subTotal, shipping } = checkout;
  const totalItems = sum(cart.map((item) => item.quantity));
  const empty = !cart.length;
  const qs = buildOrderingCheckoutQuery(searchParams);
  const checkoutUrl = `${paths.dashboard.v2.product.checkout}${qs}`;

  const deliveryFee = shipping || 10;

  return (
    <Drawer anchor="right" open={cartDrawerOpen} onClose={closeCart} PaperProps={{ sx: { width: { xs: 1, sm: 420 }, maxWidth: '100%' } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>
          My Cart
        </Typography>
        <IconButton aria-label="Close cart" onClick={closeCart}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {empty ? (
          <EmptyContent
            title="Cart is empty"
            description="Add items from the menu."
            imgUrl="/assets/icons/empty/ic_cart.svg"
            sx={{ py: 4 }}
          />
        ) : (
          <CheckoutCartProductList
            products={cart}
            onDelete={onDeleteCart}
            onIncreaseQuantity={onIncreaseQuantity}
            onDecreaseQuantity={onDecreaseQuantity}
          />
        )}

        {!empty && crossSellProducts?.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              You might also enjoy
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
              {crossSellProducts.slice(0, 8).map((p) => (
                <Box key={p.id} sx={{ width: 140, flexShrink: 0 }}>
                  <OrderV2ProductCard
                    product={p}
                    currency={p.currency || 'AED'}
                    onOpen={(prod) => {
                      openProductDetail(prod);
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {totalItems} items
            </Typography>
            <Typography variant="subtitle2">{fCurrency(subTotal)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Delivery
            </Typography>
            <Typography variant="subtitle2">{fCurrency(deliveryFee)}</Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={700}>
              Total
            </Typography>
            <Typography variant="subtitle1" fontWeight={800}>
              {fCurrency((subTotal || 0) + deliveryFee)}
            </Typography>
          </Stack>
        </Stack>
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={empty}
          onClick={() => {
            closeCart();
            navigate(checkoutUrl);
          }}
          endIcon={<Iconify icon="eva:arrow-forward-fill" />}
          sx={{ bgcolor: '#0e7c62', fontWeight: 700, '&:hover': { bgcolor: '#065f4a' } }}
        >
          Checkout
        </Button>
      </Box>
    </Drawer>
  );
}
